/**
 * Resolve crew vs imposter for UI when ``me.is_imposter`` from WS is still null.
 * Uses ``game.imposters`` from the snapshot when personal hints are delayed or missed.
 */
function normalizedImposterSet(imposters: unknown): Set<string> {
  if (!Array.isArray(imposters)) return new Set();
  return new Set(
    imposters.map((x) => String(x).trim()).filter((s) => s.length > 0),
  );
}

export function effectiveImposterOnlineRole(
  me: { is_imposter?: boolean | null } | null | undefined,
  phase: string | undefined,
  playerId: string | null | undefined,
  imposters: unknown,
): boolean | undefined {
  if (typeof me?.is_imposter === "boolean") return me.is_imposter;
  if (!playerId || phase === "lobby" || phase === "aborted_host_left" || !phase) return undefined;
  const ids = normalizedImposterSet(imposters);
  if (ids.size === 0) return undefined;
  const pid = playerId.trim();
  return ids.has(pid);
}

const PHASES_CREW_MAY_NEED_WORD = new Set(["reveal", "describe", "reveal_answers", "vote", "results"]);

/** Read crew word from ``me`` regardless of snake_case vs camelCase API quirks. */
export function extractImposterOnlineSecretWord(
  me: { secret_word?: unknown; secretWord?: unknown } | null | undefined,
): string | null {
  if (!me) return null;
  const v = me.secret_word ?? me.secretWord;
  if (v == null) return null;
  const s = typeof v === "string" ? v : String(v);
  const t = s.trim();
  return t.length > 0 ? t : null;
}

/** Crew word hints from ``me`` (parity with backend ``secret_word_hints``). */
export function extractImposterOnlineSecretWordHints(
  me: { secret_word_hints?: unknown; secretWordHints?: unknown } | null | undefined,
): string[] {
  if (!me) return [];
  const raw = me.secret_word_hints ?? me.secretWordHints;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === "string")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Keep requesting ``pull_me`` / REST hints until crew have a non-empty ``secret_word``. */
export function shouldPullImposterMeDuringReveal(
  me: { is_imposter?: boolean | null; secret_word?: string | null; secretWord?: unknown } | null | undefined,
  phase: string | undefined,
  playerId: string | null | undefined,
  imposters: unknown,
): boolean {
  if (!playerId || !phase || !PHASES_CREW_MAY_NEED_WORD.has(phase)) return false;
  const eff = effectiveImposterOnlineRole(me, phase, playerId, imposters);
  if (eff === true) return false;
  if (eff !== false) return true;
  return extractImposterOnlineSecretWord(me) == null;
}
